import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, Injector, Input, OnInit, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Params } from '@angular/router';
import { EmailOtpStepComponent } from '@components/email-otp-step/email-otp-step.component';
import { EmailStepComponent } from '@components/email-step/email-step.component';
import { FinalStepComponent } from '@components/final-step/final-step.component';
import { OtpStepComponent } from '@components/otp-step/otp-step.component';
import { PersonalDataStepComponent } from '@components/personal-data-step/personal-data-step.component';
import { GTM_EVENT_CONSTANT, PARTNER_REGISTRATION_MODE, PartnerRegistrationMode } from '@constants/app.constants';
import { PartnerFormModel } from '@models/partner-form.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '@services/analytics.service';
import { HttpClientService } from '@services/http-client.service';
import { LoggerService } from '@services/logger.service';
import { RecaptchaService } from '@services/recaptcha.service';
import { SignupRequirementsService } from '@services/signup-requirements.service';
import { environment } from '@environments/environment';

const modules = [TranslateModule];
const components = [EmailStepComponent, PersonalDataStepComponent, OtpStepComponent, EmailOtpStepComponent, FinalStepComponent];

/**
 * Shape of the validation errors the registration API returns — a map of
 * field name → (rule name → message), e.g. { email: { unique: '…' } }.
 */
type RegistrationErrors = Record<string, Record<string, string>>;

/**
 * Maps an email-policy description key to its matching title key so the
 * banner headline stays consistent with the reason shown underneath it
 * (e.g. a free-provider rejection shouldn't show the "banned domain" title).
 * Any key not listed here (the generic banned-domain case) falls back to
 * 'email_banned_domains_title'.
 */
const EMAIL_POLICY_TITLE_KEYS: Partial<Record<string, string>> = {
  email_disposable: 'email_disposable_title',
  email_free_provider: 'email_free_provider_title',
  email_domain_blocked: 'email_domain_blocked_title'
};

@Component({
  selector: 'app-create-partner',
  templateUrl: './create-partner.component.html',
  styleUrls: ['./create-partner.component.scss'],
  imports: [...modules, ...components]
})
export class CreatePartnerComponent implements OnInit {

  @Input() private lang = environment.defaultLocale;
  @Input() private language: string;
  @Input('partnership-uuid') private partnershipUUID: string;
  protected uiMode$ = input<string>('', { alias: 'ui-mode' });
  @Input('utm-source') private utmSource: string;
  @Input() private email: string;

  private translate = inject(TranslateService);
  private httpClientService = inject(HttpClientService);
  private recaptchaService = inject(RecaptchaService);
  private analyticsService = inject(AnalyticsService);
  private signupRequirementsService = inject(SignupRequirementsService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  // The registration mode (PLG/SLG) is resolved async from the brand policy, so the
  // wizard shows a brief loader instead of flashing the PLG email step before the
  // SLG form. Resolved exactly once, when the requirements fetch settles.
  protected resolving$ = signal(true);
  private modeResolved = false;

  protected readonly gtmEventConstant = GTM_EVENT_CONSTANT;
  protected _recaptchaToken: string;

  protected partnerFormModel = new PartnerFormModel();
  protected errorText$ = signal<string>(undefined);
  protected warningDescriptionText$ = signal<string>(undefined);
  protected warningTitleText$ = signal<string>(undefined);
  protected isFrame$ = signal<boolean>(undefined);
  protected activeStep$ = signal(1);
  protected partner: PartnerFormModel;
  protected personalFormError$ = signal(false);
  protected emailFormError$ = signal(false);
  protected readonly registrationMode = PARTNER_REGISTRATION_MODE;
  protected registrationMode$ = signal<PartnerRegistrationMode>(PARTNER_REGISTRATION_MODE.PLG);

  // Signup Rules P3 (phone verification & SMS OTP): mobile number the OTP
  // step should send/verify against -- the exact string the tel-input on
  // the personal-data step produced, unreformatted.
  protected otpMobile$ = signal<string>(undefined);
  // Signup Rules P5: email the email-OTP step should send/verify against.
  protected otpEmail$ = signal<string>(undefined);
  // Signup Rules P3: the backend is authoritative about the mobile being
  // required. Once it answers mobile.required we force the phone field to render
  // on the (SLG) form even if the async /requirements fetch never resolved
  // require_mobile_confirmation — otherwise a failed /requirements would dead-end SLG signups
  // with no way to enter the number. Sticky (never reset). JB-1569.
  protected mobileRequired$ = signal(false);

  // JB-1686: OTP progress cue. Email OTP is always the first verification screen;
  // the SMS OTP is screen 2 when email confirmation is also required, else 1.
  // Total = number of OTP screens the brand policy will show.
  protected otpTotal = computed(() =>
    (this.signupRequirementsService.requireEmailConfirmation() ? 1 : 0) +
    (this.signupRequirementsService.requireMobileConfirmation() ? 1 : 0));
  protected smsOtpStep = computed(() =>
    this.signupRequirementsService.requireEmailConfirmation() ? 2 : 1);

  // Signal (not a plain flag) so the template can show a "creating your account"
  // loader while POST /partners is in flight — the last OTP step otherwise stays
  // on screen with its button re-enabled while the create runs in the background.
  protected isSubmittingPartner$ = signal(false);
  // Which pre-creation verifications the user cleared this session, so the proactive
  // gate (proceedToVerificationOrCreate) doesn't loop back into an already-completed
  // OTP step. JB-1569.
  protected emailOtpVerified = false;
  private mobileOtpVerified = false;

  ngOnInit(): void {
    this.lang = this.language || this.lang;
    this.generateRecaptchaToken();
    // Brand signup requirements — drives, among other things, the phone-input
    // country list (restricted to the policy's allowed_country_codes).
    this.signupRequirementsService.load();

    const params: Params = window.location.search.substring(1).split('&').reduce((q, query) => {
      const chunks = query.split('=');
      const key = chunks[0];
      const value = chunks[1];
      return (q[key] = value, q);
    }, {});

    this.isFrame$.set(!!params.isFrame);

    // Resolve the partnership-uuid (iframe: query param, falling back to the element's
    // partnership-uuid attribute; web component: the attribute) BEFORE choosing the
    // mode — SLG depends on it.
    if (this.isFrame$()) {
      this.lang = params.lang || environment.defaultLocale;
      this.partnershipUUID = params['partnership-uuid'] || this.partnershipUUID;
      this.utmSource = params['utm-source'];
      this.email = params.email;
    }

    // Registration mode is the brand's admin-set `default_registration_mode`, read from
    // /requirements — the old `?mode=` query param is DEPRECATED (the admin UI is now the
    // single source of truth). It settles asynchronously, so resolve it once the
    // requirements fetch completes. SLG (minimal, sales-led signup) is only valid WITH a
    // resolved partnership: a partnership-less SLG is downgraded to PLG by the backend
    // anyway, and the minimal SLG form would otherwise submit with no name — rejected, and
    // able to surface another partner's data via the dedup lookup. So without a
    // partnership-uuid we fall back to PLG (the full form).
    effect(() => {
      if (this.modeResolved || !this.signupRequirementsService.loaded()) {
        return;
      }
      this.modeResolved = true;
      const wantsSlg = this.signupRequirementsService.registrationMode().toUpperCase() === PARTNER_REGISTRATION_MODE.SLG;
      const mode = (wantsSlg && this.partnershipUUID)
        ? PARTNER_REGISTRATION_MODE.SLG
        : PARTNER_REGISTRATION_MODE.PLG;
      this.registrationMode$.set(mode);
      // SLG skips the email step (step 1) — email lives on the step-2 form.
      if (mode === PARTNER_REGISTRATION_MODE.SLG) {
        this.activeStep$.set(2);
      }
      this.resolving$.set(false);
    }, { injector: this.injector });

    this.translate.setDefaultLang(this.lang);

    // Pre-select email
    if (this.email) {
      this.partnerFormModel.email = this.email;
    }
  }

  private generateRecaptchaToken(): void {
    this.recaptchaService.generateToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: token => this._recaptchaToken = token,
        error: (error: HttpErrorResponse) => LoggerService.error(error)
      });
  }

  /**
   * Re-fetches the recaptcha v3 token before the OTP-triggered resubmit.
   * The token generated in ngOnInit() is short-lived (~2 minutes) and the
   * OTP round-trip (wait for SMS, type the code) routinely exceeds that,
   * so reusing the original token here would resubmit with an already
   * expired one.
   */
  private refreshRecaptchaTokenAndCreatePartner(): void {
    this.recaptchaService.generateToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: token => {
          this._recaptchaToken = token;
          this.partnerFormModel.token = token;
          this.createPartner();
        },
        error: (error: HttpErrorResponse) => {
          LoggerService.error(error);
          // Fall back to the existing token rather than blocking the resubmit outright.
          this.createPartner();
        }
      });
  }

  protected saveEmail(email: string): void {
    this.resetErrorState();
    this.httpClientService.post(`partners/check_email`, { email: email })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.partnerFormModel.email = email;
          this.activeStep$.set(2);
          this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_1, { email });
        },
        error: (error: HttpErrorResponse) => {
          this.emailFormError$.set(true);
          if (error.status === 409) {
            this.warningDescriptionText$.set('email_error');
            this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_1_VALIDATION_FAILED, {
              error: { email: { unique: true } }
            });
          } else if (error.status === 403) {
            // Signup Rules P2 (email hardening): map the specific policy reason
            // instead of always showing the generic banned-domain message.
            const errorCode = error.error?.errors?.[0]?.error_code;
            const emailPolicyKey = errorCode === 'disposableEmail'
              ? 'email_disposable'
              : errorCode === 'freeEmail'
                ? 'email_free_provider'
                : errorCode === 'domainBlocked' || errorCode === 'domainNotAllowed'
                  ? 'email_domain_blocked'
                  : 'email_banned_domains';
            this.warningDescriptionText$.set(emailPolicyKey);
            this.warningTitleText$.set(this.emailPolicyTitleKey(emailPolicyKey));
            this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_1_VALIDATION_FAILED, {
              error: { email: { [errorCode ?? 'banned_domain']: true } }
            });
          } else {
            this.errorText$.set('exceptions.somethingwentwrong');
            this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_1_VALIDATION_FAILED, {
              error: { email: { somethingWentWrong: error } }
            });
          }
          LoggerService.error(error);
        }
      });
  }

  protected submitPartnerData(personalData: PartnerFormModel): void {
    this.resetErrorState();
    this.partnerFormModel = personalData;
    this.partnerFormModel.language_identifier = this.lang;
    this.partnerFormModel.partnership_uuid = this.partnershipUUID;
    this.partnerFormModel.utm_source = this.utmSource;
    this.partnerFormModel.token = this._recaptchaToken;
    this.partnerFormModel.registration_mode = this.registrationMode$();
    // Signup Rules P3: the tel-input on the personal-data step already
    // collects the E.164 mobile string as `phone` -- reuse it verbatim as
    // `mobile`, the field name PartnersService::createByRequest() reads for
    // the require_mobile_confirmation brand-policy gate. Do NOT reformat it.
    this.partnerFormModel.mobile = this.partnerFormModel.phone ? String(this.partnerFormModel.phone) : undefined;

    this.proceedToVerificationOrCreate();
  }

  /**
   * Proactive verification gate: /requirements already tells us which pre-creation
   * verifications the brand needs, so run those OTP steps FIRST and POST /partners
   * only once they pass — instead of firing a POST the BE rejects with email/mobile
   * otpNotVerified just to discover the same thing (that failed probe showed up in
   * the network tab). handleRegistrationErrors() stays as the fallback if
   * /requirements was stale/failed, keeping the BE authoritative. JB-1569.
   */
  private proceedToVerificationOrCreate(): void {
    // If the email was edited after it was OTP-verified (e.g. changed on the SLG
    // personal-data step while fixing the phone), the verified latch is stale —
    // drop it so the new address is re-verified instead of silently accepted for
    // the old one (the BE would bounce it as email.otpNotVerified anyway). JB-1569.
    if (this.emailOtpVerified && this.partnerFormModel.email !== this.otpEmail$()) {
      this.emailOtpVerified = false;
    }
    if (this.signupRequirementsService.requireEmailConfirmation() && !this.emailOtpVerified) {
      this.otpEmail$.set(this.partnerFormModel.email);
      this.activeStep$.set(5);
      return;
    }
    // Same for the mobile latch: if the number was edited after it was OTP-verified
    // (e.g. corrected after a failed create POST), drop the stale flag so the new
    // number is re-verified rather than submitted as verified (the BE would bounce
    // it as mobile.otpNotVerified anyway). Mirrors the email latch reset above.
    if (this.mobileOtpVerified && this.partnerFormModel.mobile !== this.otpMobile$()) {
      this.mobileOtpVerified = false;
    }
    if (this.signupRequirementsService.requireMobileConfirmation() && !this.mobileOtpVerified) {
      this.otpMobile$.set(this.partnerFormModel.mobile);
      this.activeStep$.set(4);
      return;
    }
    // All required verifications passed → refresh the (OTP-round-trip-expired)
    // recaptcha token and create the partner.
    this.refreshRecaptchaTokenAndCreatePartner();
  }

  /**
   * Re-submits the same payload after the OTP step confirms the mobile
   * number is verified. Kept as its own handler (rather than re-running
   * submitPartnerData) so we don't re-read stale @Input()s or re-trigger
   * the personal-data-step's own submit lifecycle.
   */
  protected onOtpVerified(): void {
    this.mobileOtpVerified = true;
    this.resetErrorState();
    // Send a verified-flag, never the raw number: emitGTMEvent pushes params onto
    // the global GTM dataLayer + a wildcard-origin postMessage, so PII must not
    // ride along (matches the boolean/error-key shape of the other calls here).
    this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_OTP_VERIFIED, {
      partner: { mobile_verified: true }
    });
    this.proceedToVerificationOrCreate();
  }

  /**
   * Email OTP confirmed → mark it done and re-run the proactive gate, which moves
   * on to the mobile OTP step (if require_mobile_confirmation is on) or POSTs. Mirrors onOtpVerified().
   */
  protected onEmailOtpVerified(): void {
    this.emailOtpVerified = true;
    this.resetErrorState();
    this.proceedToVerificationOrCreate();
  }

  /**
   * "Wrong email/number?" from an OTP screen — return to the step that owns the
   * field so the user can correct it. Entered data is preserved: the details
   * step re-seeds from partnerFormModel via its prefill input. PLG collects the
   * email on step 1; everything else (SLG email, the phone) is on the details
   * step (2). JB-1686.
   */
  protected onEditContact(which: 'email' | 'mobile'): void {
    this.resetErrorState();
    const target = which === 'email' && this.registrationMode$() !== PARTNER_REGISTRATION_MODE.SLG ? 1 : 2;
    this.activeStep$.set(target);
  }

  private createPartner(): void {
    if (this.isSubmittingPartner$()) {
      // Guard against double-submits (e.g. a duplicate OTP-verified re-trigger).
      return;
    }
    this.isSubmittingPartner$.set(true);

    this.httpClientService.post('partners', this.partnerFormModel)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.isSubmittingPartner$.set(false);
          // A rejected registration now arrives as 422 and is handled below.
          // This stays as a safety net for the deploy window in which an older
          // backend still answers a refusal with 200 plus an errors body —
          // without it such a response would be treated as a created partner.
          if (result?.errors) {
            this.handleRegistrationErrors(result.errors);
            return;
          }

          this.partner = result;
          this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2, {
            partner: { ...result }
          });
          this.activeStep$.set(3);
        },
        error: (error: HttpErrorResponse) => {
          this.isSubmittingPartner$.set(false);
          this.personalFormError$.set(true);
          if (error.status === HttpStatusCode.UnprocessableEntity) {
            // The registration was refused for a validation reason
            this.handleRegistrationErrors(error.error?.errors);
          } else if (error.status === HttpStatusCode.BadRequest) {
            // Token missing
            this.errorText$.set('recaptcha_token_missing_error');
            this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
              error: { token: { required: true } }
            });
          } else if (error.status === HttpStatusCode.Unauthorized) {
            // Invalid token
            this.errorText$.set('invalid_recaptcha_token_error');
            this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
              error: { token: { invalid: true } }
            });
          } else {
            this.errorText$.set('error');
            this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_SERVER_ERROR, {
              error
            });
          }
        }
      });
  }

  /**
   * Resolves the banner title for a given email-policy description key —
   * see EMAIL_POLICY_TITLE_KEYS.
   */
  private emailPolicyTitleKey(descriptionKey: string): string {
    return EMAIL_POLICY_TITLE_KEYS[descriptionKey] ?? 'email_banned_domains_title';
  }

  private resetErrorState(): void {
    this.warningTitleText$.set('email_error_title');
    this.warningDescriptionText$.set(undefined);
    this.errorText$.set(undefined);
    this.personalFormError$.set(false);
    this.emailFormError$.set(false);
  }

  /**
   * Renders a refused registration. The backend reports every validation
   * failure as a keyed errors object, so an unrecognised key must still stop
   * the wizard rather than fall through to the success path.
   */
  private handleRegistrationErrors(errors: RegistrationErrors | undefined): void {
    this.personalFormError$.set(true);

    // SLG keeps the email field on step 2; PLG collects it on step 1.
    const emailStep = this.registrationMode$() === PARTNER_REGISTRATION_MODE.SLG ? 2 : 1;

    if (errors?.email?.unique) {
      this.warningDescriptionText$.set('email_error');
      this.activeStep$.set(emailStep);
      this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
        error: { email: { unique: true } }
      });
    } else if (errors?.name?.unique) {
      this.errorText$.set('error');
      this.activeStep$.set(2);
      this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
        error: { name: { unique: true } }
      });
    } else if (errors?.mobile?.required) {
      // Signup Rules P3: brand policy requires a mobile number — back to the
      // step that owns the tel-input. Latch mobileRequired so the SLG form
      // renders the phone field even if /requirements never resolved require_mobile_confirmation.
      this.mobileRequired$.set(true);
      this.warningTitleText$.set('mobile_required_title');
      this.warningDescriptionText$.set('mobile_required_error');
      this.activeStep$.set(2);
      this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
        error: { mobile: Object.keys(errors.mobile ?? {}) }
      });
    } else if (errors?.mobile?.otpNotVerified) {
      // Signup Rules P3: mobile must be OTP-verified before the partner is
      // created — hand off to the OTP step.
      this.otpMobile$.set(this.partnerFormModel.mobile);
      this.activeStep$.set(4);
      this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
        error: { mobile: Object.keys(errors.mobile ?? {}) }
      });
    } else if (errors?.mobile?.countryNotAllowed) {
      // Signup Rules P3: brand policy rejects sign-ups from this country's
      // numbers — back to the step that owns the tel-input.
      this.warningTitleText$.set('mobile_country_not_allowed_title');
      this.warningDescriptionText$.set('mobile_country_not_allowed');
      this.activeStep$.set(2);
      this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
        error: { mobile: Object.keys(errors.mobile ?? {}) }
      });
    } else if (errors?.email?.otpNotVerified) {
      // Signup Rules P5: the email must be OTP-verified before the partner is
      // created — hand off to the email OTP step (checked before the generic
      // email branches so it is not swallowed by them).
      this.otpEmail$.set(this.partnerFormModel.email);
      this.activeStep$.set(5);
      this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
        error: { email: Object.keys(errors.email ?? {}) }
      });
    } else if (errors?.email?.domainBlocked || errors?.email?.domainNotAllowed || errors?.email?.disposableEmail || errors?.email?.freeEmail) {
      // Signup Rules P2 (email hardening): show the specific reason instead of
      // the generic email message, then back to the email-bearing step.
      const emailPolicyKey = errors.email.domainBlocked || errors.email.domainNotAllowed
        ? 'email_domain_blocked'
        : (errors.email.disposableEmail ? 'email_disposable' : 'email_free_provider');
      this.warningTitleText$.set(this.emailPolicyTitleKey(emailPolicyKey));
      this.warningDescriptionText$.set(emailPolicyKey);
      this.activeStep$.set(emailStep);
      this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
        error: { email: Object.keys(errors.email ?? {}) }
      });
    } else if (errors?.email) {
      // any other email rejection (missing, empty, malformed)
      this.warningTitleText$.set('error_title');
      this.warningDescriptionText$.set('email_error_message');
      this.activeStep$.set(emailStep);
      // Forward only the rule keys, not the raw backend error object, over the
      // GTM dataLayer + wildcard postMessage boundary (JB-1623 review).
      this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
        error: { email: Object.keys(errors.email ?? {}) }
      });
    } else {
      this.errorText$.set('error');
      this.activeStep$.set(2);
      // Forward only the field names, not the raw backend error structure.
      this.analyticsService.emitGTMEvent(this.gtmEventConstant.STEP_2_VALIDATION_FAILED, {
        error: { fields: Object.keys(errors ?? {}) }
      });
    }
  }
}